import { Alert, Button, Drawer, Spin } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useId, useRef } from 'react'

import { officeViewer, OFFICE_VIEWER_TEXTS as T } from './OfficeViewerStore'

import type { OfficeViewerStore } from './OfficeViewerStore'

/**
 * Шухляда Collabora (`SPEC-012` §7.10; легасі `FileInput.jsx:528-546`):
 * заголовок — ім'я файлу, ширина 1000, редактор на всю висоту.
 *
 * Відкриття — стандартна сторінка WOPI-хоста: прихована форма POST із
 * `access_token` і `access_token_ttl` у `iframe` (токен не потрапляє в URL
 * сторінки й історію браузера). У легасі `iframe` отримував адресу з `id`
 * користувача й правом запису прямо в рядку (D-03).
 */
export const OfficeViewer = observer(({ store }: { store: OfficeViewerStore }) => {
    const frameName = `wl-office-frame-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
    const formRef = useRef<HTMLFormElement | null>(null)
    const frameRef = useRef<HTMLIFrameElement | null>(null)
    const session = store.session

    useEffect(() => {
        const listener = (event: MessageEvent) => store.handleMessage(event)

        window.addEventListener('message', listener)

        return () => window.removeEventListener('message', listener)
    }, [store])

    useEffect(() => {
        if (!session) return

        store.attachFrame(frameRef.current)
        formRef.current?.submit()
    }, [session, store])

    return (
        <Drawer
            title={store.title}
            open={store.isOpen}
            size={Math.min(1000, window.innerWidth)}
            onClose={() => store.close()}
            destroyOnHidden
            className="wl-office-viewer"
            styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
            extra={
                store.mode === 'edit' && session?.canWrite ? (
                    <Button type="primary" disabled={!store.canSave} loading={store.saving} onClick={() => store.save()}>
                        {T.save}
                    </Button>
                ) : null
            }
        >
            {store.error ? (
                <Alert type="error" showIcon title={store.error} style={{ margin: 16 }} />
            ) : session ? (
                <>
                    <form ref={formRef} action={session.actionUrl} method="post" target={frameName} style={{ display: 'none' }}>
                        <input type="hidden" name="access_token" value={session.accessToken} readOnly />
                        <input type="hidden" name="access_token_ttl" value={String(session.accessTokenTtl)} readOnly />
                    </form>
                    <iframe
                        ref={frameRef}
                        name={frameName}
                        title={store.title}
                        data-testid="office-viewer-frame"
                        allow="clipboard-read; clipboard-write; fullscreen"
                        style={{ flex: 1, width: '100%', height: '100%', border: 0, background: 'var(--ant-color-bg-container)' }}
                    />
                </>
            ) : (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Spin />
                </div>
            )}
        </Drawer>
    )
})

/** Шухляда редактора застосунку — монтується один раз у `DefaultLayoutTemplate`. */
export const OfficeViewerHost = () => <OfficeViewer store={officeViewer} />
